<template>
  <div class="p-6">
    <div class="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
      <div>
        <h2 class="text-2xl font-bold text-gray-800">Coaching Score Review</h2>
        <p class="text-gray-600 mt-1 max-w-3xl">
          Human-in-the-loop review for flagged LLM scores. Admins see score, interpretation,
          validation flags, and assessment baseline — never chat transcripts (DSGVO).
        </p>
        <p v-if="reviewPolicy" class="text-sm text-violet-800 bg-violet-50 border border-violet-100 rounded-lg px-3 py-2 mt-3 inline-block">
          Tenant review policy: <strong>{{ reviewPolicyLabel }}</strong>
        </p>
      </div>
      <Button
        label="Refresh"
        icon="pi pi-refresh"
        severity="secondary"
        outlined
        :loading="loading"
        @click="loadReviews"
      />
    </div>

    <Card>
      <template #content>
        <div class="flex flex-wrap items-center gap-3 mb-4">
          <Select
            v-model="statusFilter"
            :options="statusOptions"
            option-label="label"
            option-value="value"
            class="w-52"
            @change="loadReviews"
          />
          <Tag
            v-if="pendingCount > 0"
            :value="`${pendingCount} pending`"
            severity="warn"
          />
        </div>

        <ProgressSpinner v-if="loading" class="mx-auto block" />
        <div v-else-if="error" class="p-4 bg-red-50 border border-red-200 rounded">
          <p class="text-red-800">{{ error }}</p>
        </div>
        <div v-else-if="!items.length" class="text-center py-12 text-gray-500">
          No scores in this queue.
        </div>
        <DataTable
          v-else
          :value="items"
          :paginator="items.length > 10"
          :rows="10"
          sort-field="scoredAt"
          :sort-order="-1"
        >
          <Column field="userDisplayName" header="User" sortable style="min-width: 160px" />
          <Column field="dimensionTitle" header="Dimension" sortable style="min-width: 160px" />
          <Column header="LLM score" style="width: 100px">
            <template #body="{ data: row }">
              <span class="font-semibold">{{ row.score?.toFixed(1) }}</span>
            </template>
          </Column>
          <Column header="Baseline" style="width: 100px">
            <template #body="{ data: row }">
              <span v-if="row.assessmentBaseline">{{ row.assessmentBaseline.score?.toFixed(1) }}</span>
              <span v-else class="text-gray-400">—</span>
            </template>
          </Column>
          <Column header="Flags" style="min-width: 180px">
            <template #body="{ data: row }">
              <div class="flex flex-wrap gap-1">
                <Tag
                  v-for="flag in row.validationFlags"
                  :key="flag"
                  :value="flagLabel(flag)"
                  severity="warning"
                  class="text-xs"
                />
                <span v-if="!row.validationFlags?.length" class="text-gray-400 text-sm">—</span>
              </div>
            </template>
          </Column>
          <Column field="reviewStatus" header="Status" sortable style="width: 130px">
            <template #body="{ data: row }">
              <Tag :value="row.reviewStatus" :severity="statusSeverity(row.reviewStatus)" />
            </template>
          </Column>
          <Column field="scoredAt" header="Scored" sortable style="width: 130px">
            <template #body="{ data: row }">
              {{ formatDate(row.scoredAt) }}
            </template>
          </Column>
          <Column header="Actions" style="width: 110px">
            <template #body="{ data: row }">
              <Button
                label="Review"
                size="small"
                :disabled="row.reviewStatus !== 'pending_review'"
                @click="openReview(row)"
              />
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>

    <Dialog
      v-model:visible="dialogVisible"
      modal
      header="Review LLM score"
      :style="{ width: 'min(720px, 95vw)' }"
      :closable="!resolving"
    >
      <div v-if="selected" class="space-y-4">
        <div class="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p class="text-gray-500">User</p>
            <p class="font-medium text-gray-900">{{ selected.userDisplayName }}</p>
            <p v-if="selected.userEmail" class="text-gray-500">{{ selected.userEmail }}</p>
          </div>
          <div>
            <p class="text-gray-500">Dimension</p>
            <p class="font-medium text-gray-900">{{ selected.dimensionTitle }}</p>
          </div>
        </div>

        <div class="grid sm:grid-cols-2 gap-4">
          <div class="p-4 rounded-lg border border-violet-100 bg-violet-50">
            <p class="text-xs font-semibold text-violet-900 uppercase mb-1">LLM score</p>
            <p class="text-2xl font-bold text-gray-900">{{ selected.score?.toFixed(1) }}/5</p>
            <p class="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{{ selected.interpretation }}</p>
            <div class="flex flex-wrap gap-1 mt-2">
              <Tag
                v-for="flag in selected.validationFlags"
                :key="flag"
                :value="flagLabel(flag)"
                severity="warning"
              />
            </div>
          </div>
          <div class="p-4 rounded-lg border border-gray-200 bg-gray-50">
            <p class="text-xs font-semibold text-gray-600 uppercase mb-1">Assessment baseline</p>
            <template v-if="selected.assessmentBaseline">
              <p class="text-2xl font-bold text-gray-900">{{ selected.assessmentBaseline.score?.toFixed(1) }}/5</p>
              <p v-if="selected.assessmentBaseline.interpretation" class="text-sm text-gray-600 mt-2">
                {{ selected.assessmentBaseline.interpretation }}
              </p>
            </template>
            <p v-else class="text-sm text-gray-500">No assessment baseline for this dimension.</p>
          </div>
        </div>

        <div v-if="selected.rubricSnippet?.length" class="p-4 rounded-lg border border-gray-200">
          <p class="text-xs font-semibold text-gray-600 uppercase mb-2">Scoring rubric</p>
          <ul class="text-sm text-gray-700 space-y-1 list-disc list-inside">
            <li v-for="(entry, index) in selected.rubricSnippet" :key="index">
              <strong>{{ entry.score }}:</strong> {{ entry.pattern }}
            </li>
          </ul>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Review note (optional)</label>
          <Textarea v-model="reviewNote" rows="2" class="w-full" :disabled="resolving" />
        </div>

        <div v-if="action === 'override'" class="space-y-3 p-4 border border-amber-200 bg-amber-50 rounded-lg">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Override score (1–5)</label>
            <InputNumber v-model="overrideScore" :min="1" :max="5" :step="0.1" :disabled="resolving" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Override interpretation</label>
            <Textarea v-model="overrideInterpretation" rows="3" class="w-full" :disabled="resolving" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
            <InputText v-model="overrideReason" class="w-full" :disabled="resolving" />
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex flex-wrap justify-end gap-2">
          <Button label="Cancel" severity="secondary" text :disabled="resolving" @click="dialogVisible = false" />
          <Button
            label="Reject"
            severity="danger"
            outlined
            :loading="resolving && action === 'reject'"
            :disabled="resolving"
            @click="submitReview('reject')"
          />
          <Button
            label="Override"
            severity="warn"
            outlined
            :disabled="resolving"
            @click="action = 'override'"
          />
          <Button
            label="Approve"
            severity="success"
            :loading="resolving && action === 'approve'"
            :disabled="resolving"
            @click="submitReview('approve')"
          />
          <Button
            v-if="action === 'override'"
            label="Save override"
            :loading="resolving"
            :disabled="resolving || !canSubmitOverride"
            @click="submitReview('override')"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script>
import Button from 'primevue/button';
import Card from 'primevue/card';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Dialog from 'primevue/dialog';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import ProgressSpinner from 'primevue/progressspinner';
import Select from 'primevue/select';
import Tag from 'primevue/tag';
import Textarea from 'primevue/textarea';
import coachingAdminService from '@/services/coachingAdminService';
import { COACHING_CONFIG_REVIEW_POLICY_OPTIONS } from '@/utils/coachingConfigOptions';

const FLAG_LABELS = {
  insufficient_evidence: 'Low evidence',
  extreme_score_low_evidence: 'Extreme score',
  baseline_divergence: 'Baseline gap',
};

export default {
  name: 'CoachingScoreReviewManager',
  components: {
    Button,
    Card,
    Column,
    DataTable,
    Dialog,
    InputNumber,
    InputText,
    ProgressSpinner,
    Select,
    Tag,
    Textarea,
  },
  data() {
    return {
      loading: false,
      resolving: false,
      error: null,
      items: [],
      pendingCount: 0,
      reviewPolicy: null,
      statusFilter: 'pending_review',
      statusOptions: [
        { label: 'Pending review', value: 'pending_review' },
        { label: 'All LLM scores', value: 'all' },
      ],
      dialogVisible: false,
      selected: null,
      action: 'approve',
      reviewNote: '',
      overrideScore: null,
      overrideInterpretation: '',
      overrideReason: '',
    };
  },
  computed: {
    reviewPolicyLabel() {
      return COACHING_CONFIG_REVIEW_POLICY_OPTIONS.find((o) => o.value === this.reviewPolicy)?.label
        || this.reviewPolicy;
    },
    canSubmitOverride() {
      return this.overrideScore != null && String(this.overrideInterpretation || '').trim().length > 0;
    },
  },
  mounted() {
    this.loadReviews();
  },
  methods: {
    flagLabel(flag) {
      return FLAG_LABELS[flag] || flag;
    },
    statusSeverity(status) {
      if (status === 'pending_review') return 'warn';
      if (status === 'approved' || status === 'auto_approved' || status === 'overridden') return 'success';
      if (status === 'rejected') return 'danger';
      return 'secondary';
    },
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString();
    },
    async loadReviews() {
      this.loading = true;
      this.error = null;
      try {
        const data = await coachingAdminService.listScoreReviews(this.statusFilter);
        this.items = data.items ?? [];
        this.pendingCount = data.pendingCount ?? 0;
        this.reviewPolicy = data.reviewPolicy ?? null;
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || 'Failed to load review queue';
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    async openReview(row) {
      this.action = 'approve';
      this.reviewNote = '';
      this.overrideScore = row.score;
      this.overrideInterpretation = row.interpretation || '';
      this.overrideReason = '';
      try {
        this.selected = await coachingAdminService.getScoreReview(row.userId, row.dimensionValue);
        this.dialogVisible = true;
      } catch (err) {
        this.$toast?.add?.({
          severity: 'error',
          summary: err?.response?.data?.message || err?.message || 'Failed to load review detail',
          life: 5000,
        });
      }
    },
    async submitReview(action) {
      if (!this.selected) return;
      if (action === 'override' && !this.canSubmitOverride) return;

      this.action = action;
      this.resolving = true;
      try {
        const payload = {
          action,
          reviewNote: this.reviewNote?.trim() || undefined,
        };
        if (action === 'override') {
          payload.overrideScore = this.overrideScore;
          payload.overrideInterpretation = this.overrideInterpretation.trim();
          payload.overrideReason = this.overrideReason?.trim() || undefined;
        }
        await coachingAdminService.resolveScoreReview(
          this.selected.userId,
          this.selected.dimensionValue,
          payload,
        );
        this.$toast?.add?.({
          severity: 'success',
          summary: `Score ${action}d`,
          life: 3000,
        });
        this.dialogVisible = false;
        await this.loadReviews();
      } catch (err) {
        this.$toast?.add?.({
          severity: 'error',
          summary: err?.response?.data?.message || err?.message || 'Review action failed',
          life: 5000,
        });
      } finally {
        this.resolving = false;
      }
    },
  },
};
</script>
